import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, MapPin, Users, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { format, isPast } from "date-fns";
import { ptBR } from "date-fns/locale";
import { supabase } from "../config/supabase";
import { useTenantSlug } from "../context/TenantSlugContext";
import { buildTenantPath } from "../utils/tenantSlug";

type EventsTab = "upcoming" | "past";

interface PublicEventRow {
  eventId: string;
  clientId: string;
  clientName: string;
  clientSlug: string;
  eventTitle: string;
  eventDate: string;
  eventLocation: string;
  eventAddress: string;
  eventDescription: string;
  eventCapacity: number;
  eventImage?: string | null;
  eventRegistrationLink?: string | null;
}

const EventsPage = () => {
  const { tenantSlug, resolvedTenant } = useTenantSlug();
  const isTenantContext = Boolean(tenantSlug);
  const [activeTab, setActiveTab] = useState<EventsTab>("upcoming");
  const [events, setEvents] = useState<PublicEventRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    let isCancelled = false;

    const fetchEvents = async () => {
      setIsLoading(true);
      setErrorMessage(null);

      try {
        const { data, error } = isTenantContext
          ? await supabase.rpc("get_public_events_by_slug", { p_slug: tenantSlug })
          : await supabase.rpc("get_public_events_marketplace");

        if (error) throw error;
        if (isCancelled) return;

        setEvents((data as PublicEventRow[] | null) ?? []);
      } catch (error) {
        console.error("Erro ao carregar eventos:", error);
        if (!isCancelled) {
          setEvents([]);
          setErrorMessage("Não foi possível carregar os eventos agora. Tente novamente em instantes.");
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    void fetchEvents();

    return () => {
      isCancelled = true;
    };
  }, [isTenantContext, tenantSlug]);

  const filteredEvents = useMemo(() => {
    if (!isTenantContext) return events;
    return events.filter((event) => {
      const eventDate = new Date(event.eventDate);
      return activeTab === "upcoming" ? !isPast(eventDate) : isPast(eventDate);
    });
  }, [activeTab, events, isTenantContext]);

  const title = isTenantContext ? "Eventos" : "Marketplace de Eventos";
  const subtitle = isTenantContext
    ? `Confira a programação de ${resolvedTenant?.clientName ?? "eventos"}`
    : "Próximos eventos ativos de todas as clínicas parceiras";

  const emptyMessage = isTenantContext
    ? activeTab === "upcoming"
      ? "Não há eventos programados no momento."
      : "Não há eventos passados para exibir."
    : "Não há eventos ativos disponíveis no marketplace no momento.";

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.45 },
    },
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">{title}</h1>
            <p className="text-lg text-gray-600">{subtitle}</p>
          </div>

          {isTenantContext && (
            <div className="mb-8 border-b border-gray-200">
              <nav className="-mb-px flex space-x-8">
                <button
                  onClick={() => setActiveTab("upcoming")}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === "upcoming"
                      ? "border-primary-500 text-primary-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  Próximos Eventos
                </button>
                <button
                  onClick={() => setActiveTab("past")}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === "past"
                      ? "border-primary-500 text-primary-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  Eventos Passados
                </button>
              </nav>
            </div>
          )}

          {isLoading ? (
            <div className="bg-white rounded-lg shadow-sm p-10 text-center text-gray-600">
              Carregando eventos...
            </div>
          ) : errorMessage ? (
            <div className="bg-white rounded-lg shadow-sm p-10 text-center text-red-600">
              {errorMessage}
            </div>
          ) : filteredEvents.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg shadow-sm">
              <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">{emptyMessage}</p>
            </div>
          ) : (
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="grid gap-8"
            >
              {filteredEvents.map((event) => {
                const isEventPast = isPast(new Date(event.eventDate));

                return (
                  <motion.div
                    key={event.eventId}
                    variants={itemVariants}
                    className="bg-white rounded-lg shadow-sm overflow-hidden"
                  >
                    <div className="grid md:grid-cols-3 gap-6">
                      <div className="md:col-span-1">
                        <img
                          src={event.eventImage || "https://images.pexels.com/photos/4386466/pexels-photo-4386466.jpeg"}
                          alt={event.eventTitle}
                          className="w-full h-64 md:h-full object-cover"
                        />
                      </div>
                      <div className="md:col-span-2 p-6">
                        {!isTenantContext && (
                          <div className="mb-3">
                            <Link
                              to={buildTenantPath("/eventos", event.clientSlug)}
                              className="inline-flex items-center rounded-full bg-primary-50 px-3 py-1 text-xs font-medium text-primary-700 hover:bg-primary-100"
                            >
                              {event.clientName}
                            </Link>
                          </div>
                        )}

                        <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                          {event.eventTitle}
                        </h3>

                        <div className="space-y-3 mb-6">
                          <div className="flex items-center text-gray-600">
                            <Calendar className="w-5 h-5 mr-2 text-primary-600" />
                            {format(new Date(event.eventDate), "dd 'de' MMMM 'de' yyyy 'às' HH:mm'h'", { locale: ptBR })}
                          </div>

                          <div className="flex items-center text-gray-600">
                            <MapPin className="w-5 h-5 mr-2 text-primary-600" />
                            {event.eventLocation} - {event.eventAddress}
                          </div>

                          <div className="flex items-center text-gray-600">
                            <Users className="w-5 h-5 mr-2 text-primary-600" />
                            Capacidade: {event.eventCapacity} pessoas
                          </div>
                        </div>

                        <p className="text-gray-600 mb-6">{event.eventDescription}</p>

                        {!isEventPast && event.eventRegistrationLink ? (
                          <a
                            href={event.eventRegistrationLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 transition-colors"
                          >
                            Inscreva-se <ArrowRight className="ml-2 w-4 h-4" />
                          </a>
                        ) : (
                          <span className="inline-flex items-center px-4 py-2 rounded-md text-sm font-medium bg-gray-100 text-gray-800">
                            Evento Encerrado
                          </span>
                        )}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EventsPage;
